import { CountrySelect, CountryTag, countryOf, useCountries, FAMILY_PALETTE } from "@/components/Country";
import { hsl } from "@/lib/color";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, Trash2, Upload, Archive, ImageDown, Instagram, Clapperboard } from "lucide-react";
import { VideoPrompts } from "@/components/VideoPrompts";
import { LaunchProduct } from "@/components/LaunchProduct";
import { RegionIntros } from "@/components/RegionIntros";
import { FeaturedPacks } from "@/components/FeaturedPacks";
import { MapDrafts } from "@/components/MapDrafts";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const aud = (n) => `A$${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 2 })}`;
const EMPTY = { name: "", region: "", country: "", guide_ids: [], price_aud: "", include_toolkit: true, family: false, description: "" };
const bestOf = (c, fam) => `Best of ${c}${fam ? " for Families" : ""}`;
const isAuto = (n) => !n || /^Best of /.test(n);

const PackForm = ({ regions: allRegions, guides: allGuides, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [mode, setMode] = useState("region");
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState("France");
  const guides = allGuides.filter((g) => !!g.family === f.family);
  const regions = allRegions.filter((r) => !!r.family === f.family);
  const countryGuides = guides.filter((g) => (g.country || "Italy") === country);
  const setFamily = (on) => setF((x) => ({ ...x, family: on, guide_ids: [], region: "", name: mode === "country" && isAuto(x.name) ? bestOf(country, on) : x.name }));
  const toggle = (id) => setF({ ...f, guide_ids: f.guide_ids.includes(id) ? f.guide_ids.filter((x) => x !== id) : [...f.guide_ids, id] });

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...f, price_aud: Number(f.price_aud), region: mode === "region" ? f.region : "", country: mode === "country" ? country : "", guide_ids: mode !== "region" ? f.guide_ids : [] };
      await api.post("/packs", body);
      toast.success("Pack created");
      setF(EMPTY);
      onCreated();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="pack-form">
      <div className="space-y-2"><Label htmlFor="pack-name">Pack name</Label>
        <Input id="pack-name" data-testid="pack-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="The Umbria Collection" className="h-11 bg-white rounded-none" /></div>
      <label data-testid="pack-family-toggle" className={`flex items-center gap-3 p-3 border text-sm cursor-pointer transition-colors ${f.family ? "border-[#A4462A] bg-[#FCEFD8] text-[#A4462A]" : "border-[#E2D9CC] text-[#3B1B48] hover:bg-[#FAF7F2]"}`}>
        <Switch data-testid="pack-family-switch" checked={f.family} onCheckedChange={setFamily} className="data-[state=checked]:bg-[#A4462A]" />Families pack: bundle only families editions, in the family colours
      </label>
      <div className="flex gap-2">
        {[["region", "Whole region"], ["country", "Country pack"], ["pick", "Pick guides"]].map(([v, l]) => (
          <button key={v} data-testid={`pack-mode-${v}`} onClick={() => { setMode(v); setF((x) => ({ ...x, guide_ids: [], name: v === "country" && isAuto(x.name) ? bestOf(country, x.family) : x.name })); }} className={`px-4 py-2 text-sm border transition-colors ${mode === v ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC] text-[#3B1B48] hover:bg-[#EFE6F3]"}`}>{l}</button>
        ))}
      </div>
      {mode === "region" ? (
        <div className="flex flex-wrap gap-2" data-testid="pack-regions">
          {regions.length === 0 && <p className="text-sm text-[#8C827A]">No finished {f.family ? "families " : ""}guides with a region yet.</p>}
          {regions.map((r) => (
            <button key={`${r.country}-${r.region}`} data-testid={`pack-region-${r.region}`} onClick={() => setF({ ...f, region: r.region })}
              className={`px-3 py-1.5 text-sm border transition-colors ${f.region === r.region ? "border-[#C59B27] bg-[#C59B27]/15 text-[#3B1B48]" : "border-[#E2D9CC] hover:bg-[#EFE6F3]"}`}>{r.region} · {r.guides}{r.country !== "Italy" && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#8C827A]">{r.country}</span>}</button>
          ))}
          <p className="w-full text-xs text-[#8C827A]">Region packs grow automatically: new guides finished in this region join the pack.</p>
        </div>
      ) : mode === "country" ? (
        <div className="space-y-3" data-testid="pack-country-picker">
          <CountrySelect testId="pack-country-select" value={country} onChange={(v) => { setCountry(v); setF((x) => ({ ...x, guide_ids: [], name: isAuto(x.name) ? bestOf(v, x.family) : x.name })); }} className="w-full" />
          <div className="max-h-56 overflow-auto border border-[#E2D9CC] bg-white">
            {countryGuides.length === 0 && <p className="text-sm text-[#8C827A] p-4">No finished {country} {f.family ? "families " : ""}guides yet.</p>}
            {countryGuides.map((g) => (
              <label key={g.id} className="flex items-center gap-3 px-4 py-2 border-b border-[#E2D9CC] last:border-0 text-sm cursor-pointer hover:bg-[#EFE6F3]/60">
                <input type="checkbox" data-testid={`pack-country-pick-${g.id}`} checked={f.guide_ids.includes(g.id)} onChange={() => toggle(g.id)} className="accent-[#3B1B48]" />
                <span className="font-display text-[#C59B27] w-8">{g.issue_number}</span>{g.town}<span className="text-xs text-[#8C827A]">{g.region}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-[#8C827A]">A country pack (e.g. "Best of France") uses only the guides you tick from that country, across any regions. It's styled in the country's colours.</p>
        </div>
      ) : (
        <div className="max-h-56 overflow-auto border border-[#E2D9CC] bg-white" data-testid="pack-guide-picker">
          {guides.map((g) => (
            <label key={g.id} className="flex items-center gap-3 px-4 py-2 border-b border-[#E2D9CC] last:border-0 text-sm cursor-pointer hover:bg-[#EFE6F3]/60">
              <input type="checkbox" data-testid={`pack-pick-${g.id}`} checked={f.guide_ids.includes(g.id)} onChange={() => toggle(g.id)} className="accent-[#3B1B48]" />
              <span className="font-display text-[#C59B27] w-8">{g.issue_number}</span>{g.town}<span className="text-xs text-[#8C827A]">{g.region}</span>
            </label>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <div className="space-y-2"><Label htmlFor="pack-price">Pack price (AUD)</Label>
          <Input id="pack-price" data-testid="pack-price-input" type="number" value={f.price_aud} onChange={(e) => setF({ ...f, price_aud: e.target.value })} className="h-11 bg-white rounded-none" /></div>
        <label className="flex items-center gap-3 text-sm text-[#3B1B48] pb-3">
          <Switch data-testid="pack-toolkit-toggle" checked={f.include_toolkit} onCheckedChange={(v) => setF({ ...f, include_toolkit: v })} />Include toolkits
        </label>
      </div>
      <div className="space-y-2"><Label htmlFor="pack-desc">Description (optional)</Label>
        <Textarea id="pack-desc" data-testid="pack-description-input" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-white rounded-none" placeholder="Leave blank for a ready-made description" /></div>
      <Button data-testid="pack-create-button" onClick={submit} disabled={busy || !f.name || !f.price_aud || (mode === "region" ? !f.region : f.guide_ids.length < 2)} className="h-12 rounded-none px-6">
        <Package className="w-4 h-4 mr-2" />{busy ? "Creating…" : "Create pack"}
      </Button>
    </div>
  );
};

const packTheme = (p, list) => {
  const c = !p.mixed_countries && p.guides.length ? countryOf(list, p.country) : null;
  if (!c && !p.family) return {};
  const P = p.family ? FAMILY_PALETTE : c.palette;
  return { "--g-dark": P.dark, "--g-accent": P.accent, "--g-mist": P.mist, "--primary": hsl(P.dark), background: P.mist, borderLeft: `6px solid ${P.accent}` };
};

const PackCard = ({ p, onChange }) => {
  const list = useCountries();
  const [busy, setBusy] = useState(false);
  const [vid, setVid] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`pack-${p.slug}`} data-country={p.mixed_countries ? "mixed" : p.country} className="bg-[#F4EFE6] border border-[#E2D9CC] p-7" style={packTheme(p, list)}>
      <div className="flex items-start gap-4 mb-3">
        <div className="flex-1">
          <p className="eyebrow mb-1">{p.family && <span data-testid={`pack-family-${p.slug}`}>Families · </span>}{p.region ? `Region · ${p.region}` : p.pack_type === "country" ? `Country · ${p.country}` : "Curated pack"} · {p.include_toolkit ? "PDF + toolkits" : "PDFs only"}</p>
          <p className="font-display text-2xl text-[var(--g-dark,#3B1B48)] flex items-center gap-3">{p.name}{!p.mixed_countries && p.guides.length > 0 && <CountryTag country={p.country} testId={`pack-country-${p.slug}`} />}</p>
        </div>
        <div className="text-right">
          <p data-testid={`pack-price-${p.slug}`} className="font-display text-3xl text-[var(--g-dark,#3B1B48)]">{aud(p.price_aud)}</p>
          <p className="text-xs text-[#8C827A]"><s>{aud(p.individual_total)}</s> · save {aud(p.savings)}</p>
        </div>
      </div>
      <p className="text-sm text-[#1C181D] mb-4">{p.description}</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {p.guides.map((g) => (
          <Link key={g.id} to={`/guides/${g.id}`} className={`px-3 py-1 text-xs border ${g.published ? "border-[#2D6A4F]/40 text-[#2D6A4F]" : "border-[#E2D9CC] text-[#8C827A]"}`}>
            <span className="font-display mr-1">{g.issue_number}</span>{g.town} · Ed. {g.edition}{g.published ? "" : " · not on store"}
          </Link>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {p.published ? (
          <Button variant="outline" data-testid={`pack-unpublish-${p.slug}`} disabled={busy} onClick={() => act(() => api.post(`/packs/${p.id}/store/unpublish`), "Pack removed from the store")} className="rounded-none"><Archive className="w-4 h-4 mr-2" />Unpublish</Button>
        ) : (
          <Button data-testid={`pack-publish-${p.slug}`} disabled={busy} onClick={() => act(() => api.post(`/packs/${p.id}/store/publish`), "Pack published to theexpatstudio.com")} className="rounded-none"><Upload className="w-4 h-4 mr-2" />Publish to theexpatstudio.com</Button>
        )}
        {p.guides.length > 1 && <Button variant="outline" data-testid={`pack-instagram-${p.slug}`} disabled={busy} onClick={() => act(() => api.post(`/map-posts/pack/${p.id}`), "Instagram post created — find it under Map stories & pack posts below")} className="rounded-none"><Instagram className="w-4 h-4 mr-2" />Make Instagram post</Button>}
        {p.guides.length > 0 && <Button variant="outline" data-testid={`pack-cover-download-${p.slug}`} disabled={busy} onClick={() => act(async () => { const r = await api.get(`/packs/${p.id}/cover.jpg`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${p.slug}-cover.jpg`; a.click(); }, "Pack cover downloaded")} className="rounded-none"><ImageDown className="w-4 h-4 mr-2" />Download cover</Button>}
        <Button variant="outline" data-testid={`pack-video-toggle-${p.slug}`} onClick={() => setVid(!vid)} className="rounded-none"><Clapperboard className="w-4 h-4 mr-2" />Video prompts</Button>
        <Button variant="ghost" data-testid={`pack-delete-${p.slug}`} disabled={busy} onClick={() => act(() => api.delete(`/packs/${p.id}`), "Pack deleted")} className="rounded-none text-[#8C827A]"><Trash2 className="w-4 h-4" /></Button>
        {p.store_sync && <span className="text-[11px] text-[#8C827A] ml-auto">{p.store_sync.ok ? "Synced" : "Sync failed"} · {new Date(p.store_sync.at).toLocaleDateString("en-AU")}</span>}
      </div>
      {vid && <div className="mt-5 border-t border-[#E2D9CC] pt-3"><VideoPrompts kind="pack" refId={p.id} compact /></div>}
      {p.guides.length > 1 && <LaunchProduct path={`/launch/pack/${p.id}`} what="pack" tid={`-${p.slug}`} onChanged={onChange} />}
    </div>
  );
};

export default function PacksPage() {
  const [packs, setPacks] = useState([]);
  const [regions, setRegions] = useState([]);
  const [guides, setGuides] = useState([]);
  const load = () => Promise.all([api.get("/packs"), api.get("/packs/regions"), api.get("/guides")])
    .then(([p, r, g]) => { setPacks(p.data); setRegions(r.data); setGuides(g.data.filter((x) => x.status === "done")); })
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="rise grid grid-cols-1 lg:grid-cols-[440px_1fr] gap-14">
      <div>
        <p className="eyebrow mb-3">theexpatstudio.com</p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#3B1B48] mb-4">Bundle packs</h1>
        <p className="text-[#8C827A] mb-10">Sell several guides together at one price, such as all your Umbrian towns. Buyers get every guide, protected with a single password, plus one email with all their links.</p>
        <PackForm regions={regions} guides={guides} onCreated={load} />
      </div>
      <div className="space-y-6" data-testid="pack-list">
        {packs.length === 0 && <p data-testid="packs-empty" className="text-[#8C827A]">No packs yet.</p>}
        {packs.map((p) => <PackCard key={p.id} p={p} onChange={load} />)}
      </div>
      <div className="lg:col-span-2 mt-12"><FeaturedPacks refreshKey={packs.map((p) => `${p.id}${p.published}`).join()} /></div>
      <div className="lg:col-span-2 mt-12"><MapDrafts /></div>
      <div className="lg:col-span-2 mt-8"><RegionIntros /></div>
    </div>
  );
}
